const ErrorCodes = {
  InsufficientPermissionsError: 'InsufficientPermissionsError',
  EntityAlreadyExistsError: 'EntityAlreadyExistsError',
  EntityNotFoundError: 'EntityNotFoundError',
  ValidationError: 'ValidationError',
  UnauthorizedError: 'UnauthorizedError',
  PaymentRequiredError: 'PaymentRequiredError',
  ConflictError: 'ConflictError',
  UnknownError: 'UnknownError',
  PreRequestError: 'PreRequestError',
  BadRequestError: 'BadRequestError'
} as const

export const ErrorCodeOptions = Object.values(ErrorCodes)
export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes]

export type DisplayErrorData = {
  name: ErrorCode
  message: string
  httpStatusCode?: number
}

export class DisplayError extends Error {
  name: ErrorCode
  httpStatusCode: number
  constructor(errorCode: ErrorCode, displayMessage: string, httpStatusCode: number) {
    super(displayMessage)
    this.name = errorCode
    this.httpStatusCode = httpStatusCode
  }

  static parse(error: Error | DisplayErrorData): DisplayError {
    const ErrorType = ErrorCodeToErrorTypeMap[error.name]
    if (ErrorType) {
      if (error.message) {
        return new ErrorType(error.message)
      } else {
        return new ErrorType()
      }
    } else {
      throw new Error(`Unable to parse ${error} as Display Error`)
    }
  }

  static tryParse(error: Error | DisplayErrorData): DisplayError | null {
    if (ErrorCodeToErrorTypeMap[error?.name]) {
      return this.parse(error)
    } else {
      return null
    }
  }

  toJSON(): DisplayErrorData {
    return {
      name: this.name,
      message: this.message,
      httpStatusCode: this.httpStatusCode
    }
  }
}

export class TypeDoesNotMatchError extends DisplayError {
  constructor(value: any, type: string, additionalInfo = '') {
    super(
      ErrorCodes.ValidationError,
      `value ${value?.toString()} with json representation ${
        value && JSON.stringify(value).substring(0, 200)
      }... of type ${typeof value} does not match type ${type} ${additionalInfo}`,
      422
    )
  }
}

export class BadRequestError extends DisplayError {
  constructor(message: string = 'Bad Request Error') {
    super(ErrorCodes.BadRequestError, message, 400)
  }
}

export class ValidationError extends DisplayError {
  constructor(message: string = 'One or more validation errors occurred') {
    super(ErrorCodes.ValidationError, message, 422)
  }
}

export class EntityNotFoundError extends DisplayError {
  constructor(message: string = 'Entity not found') {
    super(ErrorCodes.EntityNotFoundError, message, 404)
  }
}

export class EntityAlreadyExistsError extends DisplayError {
  constructor(message: string = 'This entity already exists') {
    super(ErrorCodes.EntityAlreadyExistsError, message, 409)
  }
}

export class InsufficientPermissionsError extends DisplayError {
  constructor(message: string = 'You do not have permission to perform this operation') {
    super(ErrorCodes.InsufficientPermissionsError, message, 403)
  }
}

export class UnauthorizedError extends DisplayError {
  constructor(message: string = 'User is not authorized.') {
    super(ErrorCodes.UnauthorizedError, message, 401)
  }
}

export class PaymentRequiredError extends DisplayError {
  constructor(message: string = 'Payment Required') {
    super(ErrorCodes.PaymentRequiredError, message, 402)
  }
}

export class CouldNotGetAuthTokenError extends DisplayError {
  constructor(message: string = 'Could not get auth token.') {
    super(ErrorCodes.UnauthorizedError, message, 401)
  }
}

export class ConflictError extends DisplayError {
  constructor(message: string = 'That data violates a uniqueness constraint.') {
    super(ErrorCodes.ConflictError, message, 409)
  }
}

export class UnknownError extends DisplayError {
  constructor(message: string = 'Unknown Error', errorCode: number = 500) {
    super(ErrorCodes.UnknownError, message, errorCode)
  }
}

export class PreRequestError extends DisplayError {
  constructor(message: string = 'An unexpected error occurred before the request could be completed.') {
    super(ErrorCodes.PreRequestError, message, 500)
  }
}

const ErrorCodeToErrorTypeMap = {
  InsufficientPermissionsError: InsufficientPermissionsError,
  EntityAlreadyExistsError: EntityAlreadyExistsError,
  EntityNotFoundError: EntityNotFoundError,
  ValidationError: ValidationError,
  UnauthorizedError: UnauthorizedError,
  PaymentRequiredError: PaymentRequiredError,
  ConflictError: ConflictError,
  UnknownError: UnknownError,
  PreRequestError: PreRequestError,
  BadRequestError: BadRequestError
}
